import { useFollowerInbox } from "@/src/features/following/hooks";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/src/config/react-query";
import { NotificationItem } from "@/src/models/notification.model";
import {
  collapseDuplicateJoinRequests,
  isRenderableNotification,
} from "@/src/utils/notification.utils";
import {
  getNotifications,
  markAllNotificationAsRead,
  markNotificationAsRead,
  sendNotification,
} from "./notification.service";

export type GetNotificationsParams = {
  Page: number;
  PageSize: number;
  IsUnreadOnly?: boolean;
  SortProperty?: string;
  SortIsDescending?: boolean;
};

export const useGetNotifications = (
  params: GetNotificationsParams,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: [
      "notifications",
      params.Page,
      params.PageSize,
      params.IsUnreadOnly,
      params.SortProperty,
      params.SortIsDescending,
    ],
    queryFn: () => getNotifications(params),
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  });
};

/**
 * How many unread rows to scan when reconciling the badge against what the list
 * will actually render. Large enough to cover any realistic unread backlog while
 * staying a single request.
 */
const UNREAD_SCAN_PAGE_SIZE = 50;

/**
 * Unread count for the nav badge.
 *
 * `GetNotifications` already returns `unReadNotificationsCount` alongside the page,
 * so this needs no extra endpoint. The query key stays under "notifications", which
 * is what the SignalR handler invalidates, so the badge updates on delivery without
 * its own wiring.
 *
 * The server's count includes records the list hides — platform rows with no
 * displayable message (see isRenderableNotification). Counting those would show a
 * badge the user can neither read nor clear, so they are subtracted.
 *
 * The page is scanned rather than counted server-side because no endpoint can
 * express "unread and displayable". If the unread backlog ever exceeds
 * UNREAD_SCAN_PAGE_SIZE, only the scanned portion is reconciled and the badge can
 * still overcount — better than under-reporting real unread notifications.
 */
export const useUnreadNotificationCount = (enabled = true) => {
  const followerInbox = useFollowerInbox(true, enabled);
  const { data } = useGetNotifications(
    { Page: 0, PageSize: UNREAD_SCAN_PAGE_SIZE, IsUnreadOnly: true },
    { enabled },
  );

  const serverCount: number = data?.unReadNotificationsCount ?? 0;
  const unread = (data?.notifications ?? []) as NotificationItem[];
  const hidden = unread.filter((n) => !isRenderableNotification(n)).length;

  // The list folds repeated join requests from the same person for the same
  // group into one row, so the superseded ones must not be counted here either
  // — otherwise the badge shows a number the inbox cannot account for.
  const renderableUnread = unread.filter(isRenderableNotification);
  const superseded =
    renderableUnread.length -
    collapseDuplicateJoinRequests(renderableUnread).length;

  return (
    Math.max(0, serverCount - hidden - superseded) +
    (followerInbox.isError
      ? 0
      : (followerInbox.data?.pages[0]?.unreadCount ?? 0))
  );
};

export const useMarkNotificationAsRead = () => {
  return useMutation({
    mutationFn: markNotificationAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
};

export const useMarkAllNotificationAsRead = () => {
  return useMutation({
    mutationFn: markAllNotificationAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
};

export const useSendNotification = () => {
  return useMutation({
    mutationFn: sendNotification,
  });
};
